import React, { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Badge, Tooltip, theme as antdTheme, Grid, Drawer, Button } from 'antd';
import type { MenuProps } from 'antd';
import { useAppContext } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import usePagePermission from '../hooks/usePagePermission';
import {
    UserOutlined,
    MenuOutlined,
    LogoutOutlined,
    ArrowLeftOutlined,
    FileTextOutlined,
    QuestionCircleOutlined,
    DashboardOutlined,
    SettingOutlined,
    FormOutlined,
} from '@ant-design/icons';

import lightLogo from '../static/cdrpl.png';
import darkLogo from '../static/cdrpl.png';

const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
    collapsible: boolean;
    isMobile?: boolean;
    isSmallDesktop?: boolean;
}

const LandStageNavigation = React.memo(() => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const isSmallDesktop = screens.md && !screens.lg; // 768px to 1200px
    return <Sidebar collapsible={!isMobile} isMobile={isMobile} isSmallDesktop={isSmallDesktop} />;
});

const Sidebar = React.memo(({ collapsible, isMobile, isSmallDesktop }: SidebarProps) => {
    const location = useLocation();
    const { state: stateApp, appContextAction } = useAppContext();
    const { isNavMenuClose } = stateApp;
    const { navMenu } = appContextAction;
    const { theme } = useTheme();
    const { user, logout } = useAuth();
    const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
    const logoPath = isDarkMode ? darkLogo : lightLogo;

    const [drawerVisible, setDrawerVisible] = useState(false);

    const canReadDashboardPermission = usePagePermission("land-dashboard", "read");
    const canReadDocumentsPermission = usePagePermission("land-documents", "read");
    const canReadQuestionnairePermission = usePagePermission("land-questionnaire", "read");
    const canReadSettingsPermission = usePagePermission("landstage-settings", "read");
    const isClientUser = user?.isclient === true;

    const canReadDashboard = isClientUser || canReadDashboardPermission;
    const canReadDocuments = isClientUser || canReadDocumentsPermission;
    const canReadQuestionnaire = isClientUser || canReadQuestionnairePermission;
    const canReadSettings = isClientUser || canReadSettingsPermission;

    const getThemeStyles = useMemo(() => ({
        sidebarHeader: {
            padding: isNavMenuClose && !isMobile ? '8px' : '0 16px',
            display: 'flex',
            flexDirection: isNavMenuClose && !isMobile ? 'column' as const : 'row' as const,
            alignItems: 'center',
            justifyContent: isNavMenuClose && !isMobile ? 'center' : 'space-between',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            borderBottom: 'none',
            backgroundColor: 'transparent'
        },
        logoContainer: {
            display: 'flex',
            alignItems: 'center',
            paddingBottom: isNavMenuClose && !isMobile ? '8px' : '0',
            justifyContent: isNavMenuClose && !isMobile ? 'center' : 'flex-start',
            marginBottom: isNavMenuClose && !isMobile ? '8px' : '0'
        },
        logo: {
            width: isNavMenuClose && !isMobile ? '40px' : (isSmallDesktop ? '140px' : '160px'),
            height: 'auto',
            transition: 'width 0.3s ease'
        },
        toggleButton: {
            color: isDarkMode ? '#fff' : '#000',
            background: 'transparent',
            marginTop: isNavMenuClose && !isMobile ? '12px' : undefined
        },
        userEmail: {
            fontSize: '12px',
            opacity: 0.85,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)'
        },
        collapsedProfile: {
            padding: '16px 0',
            display: 'flex',
            justifyContent: 'center',
            borderBottom: 'none',
            marginBottom: '8px',
            backgroundColor: 'transparent'
        },
        navigationMenu: {
            flex: 1,
            overflowY: 'auto' as const,
            overflowX: 'hidden' as const,
            padding: '0',
            height: 'auto',
            minHeight: 0
        },
        sidebarStyle: {
            boxShadow: 'none',
            zIndex: 1000,
            height: '100vh',
            position: 'fixed' as const,
            left: 0,
            display: 'flex',
            flexDirection: 'column' as const,
            transition: 'all 0.2s ease',
            background: 'transparent',
            backgroundColor: 'transparent',
            overflow: 'hidden',
            ...(isSmallDesktop && !isNavMenuClose && {
                width: '180px !important'
            })
        }
    }), [isDarkMode, isNavMenuClose, isMobile, isSmallDesktop]);

    const currentPath = useMemo(() => {
        const path = location.pathname;
        
    if (path === '/land-stage/dashboard') return 'dashboard';
    if (path === '/land-stage/documents') return 'documents';
    if (path === '/land-stage/questionnaire') return 'questionnaire';
    if (path === '/land-stage/settings') return 'settings';
        const segments = path.split('/').filter(Boolean);
        if (path === '/land-stage/input-form') return 'input-form';
        return segments.length > 0 ? segments[segments.length - 1] : '';
    }, [location.pathname]);

    const menuItems: MenuProps['items'] = useMemo(() => {
        const items: MenuProps['items'] = [];

    const primaryLinks: MenuProps['items'] = [];

        if (canReadDashboard) {
            primaryLinks.push({
                key: 'dashboard',
                icon: <DashboardOutlined />,
                label: <Link to="/land-stage/dashboard">Dashboard</Link>
            });
        }

        if (canReadDocuments) {
            primaryLinks.push({
                key: 'documents',
                icon: <FileTextOutlined />,
                label: <Link to="/land-stage/documents">Documents</Link>
            });
        }

        if (canReadQuestionnaire) {
            primaryLinks.push({
                key: 'questionnaire',
                icon: <QuestionCircleOutlined />,
                label: <Link to="/land-stage/questionnaire">Questionnaire</Link>
            });
        }

        if (canReadDocuments) {
            primaryLinks.push({
                key: 'input-form',
                icon: <FormOutlined />,
                label: <Link to="/land-stage/input-form">Input Form</Link>
            });
        }

        items.push(...primaryLinks);

        if (primaryLinks.length > 0) {
            items.push({ type: 'divider' });
        }

        if (canReadSettings) {
            items.push({ 
                key: 'settings', 
                icon: <SettingOutlined />, 
                label: <Link to="/land-stage/settings">Settings</Link> 
            });
        }

        items.push({ 
            key: 'back-to-stages', 
            icon: <ArrowLeftOutlined />, 
            label: isClientUser
                ? <Link to="/client-stage-selection">Back to Stages</Link>
                : <Link to="/project-selection">Back to Previous</Link>
        });

        items.push({ 
            key: 'logout', 
            icon: <LogoutOutlined />, 
            label: 'Logout', 
            onClick: logout, 
            danger: true 
        });
        
        return items;
    }, [
        canReadDashboard,
        canReadDocuments,
        canReadQuestionnaire,
        canReadSettings,
        isClientUser,
        logout
    ]);

    const toggleCollapse = useCallback(() => {
        if (isNavMenuClose) {
            navMenu.expand();
        } else {
            navMenu.collapse();
        }
    }, [isNavMenuClose, navMenu]);

    const handleMenuClick = useCallback((info: any) => {
        if (info.key === 'logout') {
            logout();
        }

        if (isMobile) {
            setDrawerVisible(false);
        }
    }, [logout, isMobile]);

    const toggleDrawer = useCallback(() => {
        setDrawerVisible(prev => !prev);
    }, []);

    const SidebarContent = useMemo(() => (
        <div className="transparent-nav" style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <div className="sidebar-header" style={getThemeStyles.sidebarHeader}>
                <div className="logo-container" style={getThemeStyles.logoContainer}>
                    <img
                        src={logoPath}
                        alt="CDRPL"
                        style={getThemeStyles.logo}
                    />
                </div>
            </div>            

            {isNavMenuClose && !isMobile && (
                <div style={getThemeStyles.collapsedProfile}>
                    <Tooltip title={user?.name || 'User'} placement="right">
                        <Badge dot status="success" offset={[-4, 28]}>
                            <Avatar
                                icon={<UserOutlined />}
                                size={36}
                            />
                        </Badge>
                    </Tooltip>
                </div>
            )}

            <div className="navigation-menu" style={getThemeStyles.navigationMenu}>
                <Menu
                    mode="inline"
                    selectedKeys={[currentPath]}
                    defaultOpenKeys={isMobile ? ['main', 'land', 'settings'] : []}
                    items={menuItems}
                    onClick={handleMenuClick}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        height: '100%'
                    }}
                    theme={isDarkMode ? 'dark' : 'light'}
                />
            </div>
        </div>
    ), [
        getThemeStyles,
        logoPath,
        isNavMenuClose,
        isMobile,
        user,
        currentPath,
        menuItems,
        handleMenuClick,
        isDarkMode
    ]);

    if (isMobile) {
        return (
            <>
                <Button
                    type="primary"
                    shape="circle"
                    icon={<MenuOutlined />}
                    onClick={toggleDrawer}
                    style={{
                        position: 'fixed',
                        top: '16px',
                        left: '16px',
                        zIndex: 999,
                        boxShadow: '0 3px 6px rgba(0,0,0,0.2)'
                    }}
                    size="middle"
                />
                <Drawer
                    placement="left"
                    onClose={toggleDrawer}
                    open={drawerVisible}
                    width={320}
                    styles={{
                        body: {
                            padding: 0,
                            height: '100%',
                            background: 'transparent',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                    closable={false}
                >
                    {SidebarContent}
                </Drawer>
            </>
        );
    }

    return (
        <Sider
            collapsible
            collapsed={isNavMenuClose}
            onCollapse={toggleCollapse}
            className="navigation-sidebar transparent-nav"
            theme={isDarkMode ? 'dark' : 'light'}
            width={isSmallDesktop ? 180 : 220}
            collapsedWidth={70}
            // Ensure inline style forces transparency over antd default background
            style={getThemeStyles.sidebarStyle}
            trigger={null}
        >
            {SidebarContent}
        </Sider>
    );
});

export default LandStageNavigation;
